import { useTranslations } from '@vocab/react';
import {
  Bleed,
  Box,
  ButtonIcon,
  IconChevron,
  IconFilter,
  Inline,
  Spread,
  Stack,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import type { FC, ReactElement } from 'react';

import type { FilterFns } from 'src/components/app/FilterSideBar/useFilters';

import translations from './.vocab';

import * as styles from './styles.css';

interface FilterDesktopProps {
  content: ReactElement;
  shouldShowClearFilterButton: boolean;
  resetFilters: FilterFns['resetFilters'];
  selectedFilterCount: number;
  isFilterPanelCollapsed: boolean;
  toggleFilterPanelCollapsed: () => void;
}

export const FilterDesktop: FC<FilterDesktopProps> = ({
  shouldShowClearFilterButton,
  content,
  resetFilters,
  selectedFilterCount,
  isFilterPanelCollapsed,
  toggleFilterPanelCollapsed,
}) => {
  const { t } = useTranslations(translations);

  return (
    <Box
      className={
        isFilterPanelCollapsed
          ? [
              styles.collapsedFilterDrawerSize,
              styles.collapsedFilterDrawerSpacing,
              styles.collapsedFilterDrawer,
            ]
          : [
              styles.filterDrawerSize,
              styles.filterDrawerSpacing,
              styles.filterDrawer,
            ]
      }
    >
      {isFilterPanelCollapsed ? (
        <Box display="flex" position="relative">
          <ButtonIcon
            onClick={() => toggleFilterPanelCollapsed()}
            icon={<IconFilter tone="neutral" />}
            tone="neutral"
            id="filter_drawer_expand_button"
            label={t('Open filters')}
            variant="soft"
            size="standard"
            bleed
          />

          {selectedFilterCount > 0 ? (
            <Bleed vertical="small">
              <Box
                position="absolute"
                borderRadius="full"
                justifyContent="center"
                background="formAccent"
                display="flex"
                alignItems="center"
                style={{
                  width: '12px',
                  height: '12px',
                  top: -4,
                  right: -16,
                  color: 'white',
                  font: 'icon',
                  fontSize: '10px',
                }}
                padding="xsmall"
                aria-label={t('selectedFilterCount filters are applied', {
                  selectedFilterCount,
                })}
              >
                {selectedFilterCount}
              </Box>
            </Bleed>
          ) : null}
        </Box>
      ) : (
        <Box
          background="surface"
          onScroll={(event: any) => {
            const target = event?.nativeEvent?.target;
            if (target) {
              const { top } = target.getBoundingClientRect();

              if (top > 0) {
                target.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }}
        >
          <Stack space="medium">
            <Spread space="large" alignY="center">
              <Inline space="xsmall" alignY="center">
                <Text data={{ intercomid: 'byFilters' }} weight="medium">
                  {t('Filters')}
                </Text>

                {selectedFilterCount ? (
                  <Box
                    display="flex"
                    borderRadius="full"
                    justifyContent="center"
                    background="formAccent"
                    alignItems="center"
                    style={{
                      width: '22px',
                      height: '22px',
                    }}
                    aria-label={t('selectedFilterCount filters are applied', {
                      selectedFilterCount,
                    })}
                  >
                    <Text size="xsmall">{selectedFilterCount}</Text>
                  </Box>
                ) : null}
              </Inline>

              <Inline space="small" alignY="center">
                {shouldShowClearFilterButton ? (
                  <Text size="small" weight="strong">
                    <TextLinkButton
                      id="clear_all_button"
                      onClick={() =>
                        resetFilters({
                          eventName: 'clear_filters_pressed',
                          actionOrigin: 'candidate listing - filter section',
                        })
                      }
                    >
                      {t('Clear all')}
                    </TextLinkButton>
                  </Text>
                ) : null}
                <Bleed vertical="xsmall">
                  <ButtonIcon
                    onClick={() => {
                      toggleFilterPanelCollapsed();
                    }}
                    icon={<IconChevron direction="left" />}
                    tone="neutral"
                    id="filter_drawer_collapse_button"
                    label={t('Close filters')}
                    variant="soft"
                    size="small"
                  />
                </Bleed>
              </Inline>
            </Spread>
            {content}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
